import { useRef } from "react";
import {
  useFieldArray,
  Controller as ReactHookFormController,
  useFormContext,
} from "react-hook-form";
import { Label, Row } from "reactstrap";
import { LabelValue } from "@Component/Value";
import { useTranslation } from "react-i18next";
import { Col } from "react-bootstrap";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { Controller } from "@/CommonElements/Controller";
import TableC from "@/CommonElements/TableC";
import { useCachedBasicData } from "@utils/hooks/useCachedBasicData";
import { InputNumber, Space, Tooltip } from "antd";
import { HugeiconsIcon } from "@hugeicons/react";
import { Cancel01Icon } from "@hugeicons/core-free-icons";
import {
  Bus03Icon,
  Delete02Icon,
  ServerStack02Icon,
} from "@hugeicons/core-free-icons";
import { getDublication } from "@/utils/helpers";
import { LI, UL } from "@/AbstractElements";
import {
  useDepartAirportMutation,
  useDeleteBusCardMutation,
  useMarkBusNotDepartedMutation,
} from "@services/arrivals";
import dayjs from "dayjs";
import { useScreenSize } from "@/utils/hooks/useScreenSize ";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions/SelectCachedOptions";


export const defaultBusCard = {
  bus_id: null,
  capacity: null,
  departure_date: null,
  departure_time: null,
  serverValue: false,
};
export const BusesForm = (props) => {
  const busSelectRefs = useRef({});
  //========
  // Hooks
  //========
  const { isDesktop } = useScreenSize();
  const { arrival, params, selectedArrivalIndex } = props;
  const { t } = useTranslation();
  const { getLocalizedDate, getLocalizedNumber } = useLocalizedText();
  const { cachedBasicData } = useCachedBasicData();

  const form = useFormContext();

  const {
    control: serverBusesControl,
    formState: { errors },
  } = form;

  const { fields, append, remove } = useFieldArray({
    rules: {
      minLength: 1,
      maxLength: 5,
      validate: (value) => {
        // Check for duplicates based on a specific field (e.g., 'id' or 'name')
        const values = value.map((item) => item.bus_id);
        const hasDuplicates = values.length !== new Set(values).size;

        return !hasDuplicates || t("DUPLICATE_BUS_NUMBERS");
      },
    },
    control: serverBusesControl,
    name: "bus_cards",
  });

  //========
  // Mutations
  //========
  const { mutate: mutateDepartAirport, isPending: isDepartAirportPending } =
    useDepartAirportMutation(params);

  const { mutate: mutateDeleteBusCard } = useDeleteBusCardMutation(params);
  const {
    mutate: mutateMarkBusNotDeparted,
    isPending: isMarkBusNotDepartedPending,
  } = useMarkBusNotDepartedMutation(params);


  const isDefaultRow = (index) => {
    const lastIndex = fields.length - 1;
    const lastRowValue = fields?.[lastIndex];
    return index === lastIndex && !lastRowValue?.bus_id;
  };

  //========
  // Columns
  //========
  const columns = [
    {
      name: <Label className="required">{t("BUS_NUMBER")} </Label>,
      key: "bus_id",
      colSize: { xs: 12 },
      render: ($, $$, index) => (
        <ReactHookFormController
          rules={
            isDefaultRow(index)
              ? undefined
              : {
                required: t("FIELD_REQUIRED"),
                validate: (v, state) => {
                  const dublication =
                    getDublication(state.bus_cards, "bus_id") ?? [];
                  return dublication.findIndex((i) => i === index) > -1
                    ? t("DUPLICATE_BUS_NUMBERS")
                    : true;
                },
              }
          }
          name={`bus_cards.${index}.bus_id`}
          control={serverBusesControl}
          render={({ field }) => {
            const error = errors?.bus_cards?.[index]?.bus_id;

            return (
              <Tooltip
                color="red"
                placement={isDesktop ? "left" : "top"}
                open={error}
                title={error?.message}
              >
                <SelectCachedOptions
                  {...field}
                  showSearch
                  ref={(el) => { busSelectRefs.current[index] = el; }}

                  onChange={(value) => {
                    field.onChange(value);
                    if (fields.length < 5 && index === fields.length - 1) {
                      setTimeout(() => {
                        append(defaultBusCard, {
                          shouldFocus: false
                        });
                      }, 0);
                    }
                    setTimeout(() => {
                      fields.forEach((_, i) => {
                        form.trigger([`bus_cards.${i}.bus_id`]);
                      });
                    }, 0);
                  }}
                  // onChange={(value) => {
                  //   field.onChange(value);
                  //   fields.length < 5 &&
                  //     index === fields.length - 1 &&
                  //     append(defaultBusCard);

                  //   fields.forEach(($, index) => {
                  //     form.trigger([`bus_cards.${index}.bus_id`]);
                  //   });
                  // }}
                  style={{ minWidth: isDesktop ? "320px" : "100%" }}
                  placeholder={t("SELECT_BUS_NUMBER_PLACEHOLDER")}
                  optionKey="buses"
                  options={(bus) => ({
                    value: bus.bus_id,
                    label: [
                      getLocalizedNumber(bus.bus_number),
                      bus.company_name,
                    ].join(" - "),
                  })}
                  status={
                    errors?.bus_cards?.[index]?.bus_id ? "error" : undefined
                  }
                />
              </Tooltip>
            );
          }}
        />
      ),
    },
    {
      name: t("VEHICLE_PLATE_NUMBER"),
      key: "plate_no",
      render: ($, $$, index) => {
        const busNumber = form.watch(`bus_cards.${index}.bus_id`);
        return (
          <LabelValue.Value
            value={
              cachedBasicData?.buses?.find((bus) => bus.bus_id === busNumber)
                ?.plate_no ?? "-"
            }
          />
        );
      },
    },
    {
      name: <Label className="required">{t("PASSENGER_COUNT")} </Label>,
      key: "capacity",
      render: ($, $$, index) => {
        const error = errors?.bus_cards?.[index]?.capacity;
        return (
          <ReactHookFormController
            render={({ field }) => (
              <Tooltip
                color="red"
                placement={isDesktop ? "left" : "top"}
                open={error}
                title={error?.message}
              >
                <InputNumber
                  min={1}
                  max={50}
                  {...field}
                  placeholder={t("PASSENGER_COUNT_PLACEHOLDER")}
                  status={error ? "error" : undefined}
                />
              </Tooltip>
            )}
            rules={
              isDefaultRow(index)
                ? undefined
                : {
                  required: t("FIELD_REQUIRED"),
                  min: {
                    value: 1,
                    message: t("VALIDATION_MIN_VALUE", { number: 1 }),
                  },
                  max: {
                    value: 250,
                    message: t("VALIDATION_MAX_VALUE", { number: 250 }),
                  },
                }
            }
            name={`bus_cards.${index}.capacity`}
            control={serverBusesControl}
          />
        );
      },
    },
    {
      name: t("DEPARTURE_TIME"),
      key: "departure_time",
      render: ($, $$, index) => {
        const departureTime = form.watch(`bus_cards.${index}.departure_time`);

        return (
          <LabelValue.Value
            loading={isDepartAirportPending || isMarkBusNotDepartedPending}
            value={departureTime ?? "-"}
          />
        );
      },
    },
    {
      name: t("ACTIONS"),
      key: "actions",
      disbaled: true,
    },
  ];

  //========
  // Actions
  //========
  const getActions = (rowData, index) => {
    const departureTime = form.watch(`bus_cards.${index}.departure_time`);
    const busNumber = form.watch(`bus_cards.${index}.bus_id`);

    // Last row is only disabled if it has no bus_id (truly empty/default row)
    const disabled = fields.length - 1 === index && !busNumber;

    return [
      departureTime && {
        key: "cancel",
        label: (
          <Space direction="horizontal" gap={2} align="center">
            <HugeiconsIcon className="d-flex" size={20} icon={Cancel01Icon} />
            <span>{t("CANCEL")}</span>
          </Space>
        ),
        onClick: () => handleCancel(index),
        disabled,
      },
      !departureTime && {
        key: "edit",
        label: (
          <Space direction="horizontal" gap={2} align="center">
            <HugeiconsIcon className="d-flex" size={20} icon={Bus03Icon} />
            <span>{t("START_DEPARTURE")}</span>
          </Space>
        ),
        onClick: () =>
          rowData.serverValue
            ? handleDepartAirport(rowData, index)
            : form.setValue(
              `bus_cards.${index}.departure_time`,
              dayjs(new Date()).format("HH:mm:ss")
            ),
        disabled,
      },
      {
        type: "divider",
      },
      {
        key: "delete",
        label: (
          <Space direction="horizontal" gap={2} align="center">
            <HugeiconsIcon size={20} icon={Delete02Icon} />
            <span>{t("DELETE")}</span>
          </Space>
        ),
        onClick: () => handleDelete(index),
        disabled,
        danger: true,
      },
    ];
  };
  const handleCancel = (index) => {
    if (fields[index].serverValue) {
      mutateMarkBusNotDeparted(
        {
          busCardId: fields[index].serverId,
          arrivalIndex: selectedArrivalIndex,
          busCardIndex: index,
        },
        {
          onSuccess: () => {
            form.setValue(`bus_cards.${index}.departure_time`, null);
          },
        }
      );
    } else {
      form.setValue(`bus_cards.${index}.departure_time`, null);
    }
  };
  const handleDepartAirport = (rowData, index) => {
    mutateDepartAirport(
      {
        busCardId: rowData.serverId,
        arrivalIndex: selectedArrivalIndex,
        busCardIndex: index,
      },
      {
        onSuccess: (data) => {
          form.setValue(
            `bus_cards.${index}.departure_time`,
            data.data.data.departure_time
          );
        },
      }
    );
  };

  const handleDelete = (index) => {
    if (fields[index].serverValue) {
      mutateDeleteBusCard(
        {
          busCardId: fields[index].serverId,
          arrivalIndex: selectedArrivalIndex,
        },
        {
          onSuccess: () => {
            remove(index);
          },
        }
      );
    } else {
      remove(index);
    }

    const newFields = form.watch("bus_cards");

    const LastElementValue = newFields?.[newFields.length - 1]?.bus_id;
    !!LastElementValue && append(defaultBusCard, {
      shouldFocus: false
    });
  };

  //========
  // Render
  //========
  return (
    <>
      <Row>
        <Col md={6}>
          <LabelValue
            direction="vertical"
            label={t("PILGRIMS_COUNT")}
            value={arrival?.hjaj_number || ""}
          />
        </Col>
        <Col md={6}>
          <LabelValue
            direction="vertical"
            label={t("RECEPTION_CARD_EXPECTED_ARRIVAL")}
            value={
              getLocalizedDate(arrival?.scheduled_at, "DD - MMMM YYYY HH:mm") ||
              "-"
            }
          />
        </Col>
        <Controller
          controllerProps={{
            errors,
            name: "direction_id",
            control: serverBusesControl,
            render: ({ field }) => (
              <SelectCachedOptions
                {...field}
                showSearch
                optionKey="directions"
                filter={(d) => d.id === 1 || d.id === 2 || d.id === 6}
                options={(direction) => ({
                  value: String(direction.id),
                  label: <LabelValue.Value value={direction.name} />,
                })}
                status={errors?.direction_id ? "error" : undefined}
              />
            ),
            rules: {
              required: t("FIELD_REQUIRED"),
            },
          }}
          labelProps={{
            children: t("RECEPTION_CARD_DIRECTION_LABEL"),
            className: "required fw-bold mt-4",
            htmlFor: "direction_id",
          }}
        />

        {/* Server Errors */}
        <UL md={12} style={{ listStyle: "" }}>
          {errors?.port?.message && (
            <ServerErrorItem errorMessage={errors?.port?.message} />
          )}
          {errors?.campaign_id?.message && (
            <ServerErrorItem errorMessage={errors?.campaign_id?.message} />
          )}
          {errors?.bus_cards?.root?.message && (
            <ServerErrorItem
              icon={Bus03Icon}
              errorMessage={errors?.bus_cards?.root?.message}
            />
          )}
        </UL>
      </Row>

      <TableC
        columnsFilter={false}
        data={fields || []}
        columns={columns}
        actions={getActions}
        getRowStyle={(rowData, index) => {
          const busNumber = form.watch(`bus_cards.${index}.bus_id`);
          if (rowData.serverValue === true) {
            return { background: "hsl(29deg 37% 77% / 10%)" };
          }
          if (rowData.serverValue === false && !!busNumber) {
            return { background: "hsl(141 78% 98% / 1)" };
          }
          return {};
        }}
      />
    </>
  );
};

const ServerErrorItem = ({ errorMessage, icon = ServerStack02Icon }) => (
  <LI attrLI={{ className: "border-danger" }}>
    <Space direction="horizontal" gap={2} align="center">
      <HugeiconsIcon className="text-danger d-flex" size={12} icon={icon} />
      <span className="text-danger d-flex">
        <LabelValue.Value value={errorMessage} />
      </span>
    </Space>
  </LI>
);
